// TUT-460 — a busca de cada rota: os tipos da query string tipada e os
// validadores (`validateSearch`) que a leem como lista fechada.
import type { ProcessSortColumn } from "../lib/process-sorting";
import type {
  PendenciaQueFiltra,
  RecorteDaElaboracao,
  SelecaoDaElaboracao,
} from "../lib/etapas";
import type { CanalDaConversa, LegalBriefStatusName, LegalBriefTypeKey, VehicleTripMode } from "../lib/types";
import type { TipoDeItem } from "@brevi/ui-kit/cor-do-tipo";
import type { FiltroDeEntrega } from "../lib/recorte-da-entrega";
import type { ProcessTab } from "../pages/processo/process-tabs";
import { isColunaDaLista, isDirecaoDeOrdenacao } from "../lib/list-sorting";
import type { ListSortDirection, ListaOrdenavel } from "../lib/list-sorting";
import type { ChaveDeFoco } from "../lib/foco";
import type { VenceDePrazo } from "../lib/indicadores";
import type { AgendaKind, AgendaView } from "../lib/agenda-views";
import { TRIP_MODES } from "../lib/transport-modes";
import { diaDeCalendario } from "../lib/periodo-de-datas";
import type { EtapaDeLaudo } from "../lib/recorte-de-laudos";
import { isFaixaDeContaAPagar } from "../lib/contas-a-pagar";
import { isModalidadeDeProposta, isStatusDeProposta } from "../lib/propostas-filtros";
import {
  isFaixaDoVencimento,
  isMarcoDeContaAReceber,
  isRotaDeContaAReceber,
  isSituacaoDeContaAReceber,
} from "../lib/contas-a-receber";

// ---------------------------------------------------------------------------
// Tipos da query string tipada (`validateSearch`).
//
// O TIPO declara só as chaves da rota; o passthrough de parâmetro desconhecido
// acontece em RUNTIME, pelo spread do `validateSearch` — é o que preserva o
// contrato de que a página não é dona da query inteira (TUT-080 fase 2e) sem
// quebrar a inferência do TanStack (índice de string colapsa o schema para
// `never`, medido nesta migração).
// ---------------------------------------------------------------------------

/**
 * TUT-288 — Meu perfil e Usuários ganharam o trilho de seções das
 * Configurações, com o mesmo `?secao=` em texto cru e a mesma revalidação no
 * ponto de leitura (o catálogo mora em cada tela).
 */

// ---------------------------------------------------------------------------
// TUT-204g — os filtros de cada lista, tipados.
//
// Até esta fatia estes seis viajavam CRUS: a página gravava `next.set("papel",
// …)` e lia `params.get("papel")`, e a rota nunca soube que a chave existia.
// Não era link quebrado — o `useUrlSearchParams` mescla e sempre mesclou —,
// era ausência de contrato: nada validava o valor, o TypeScript não conhecia a
// chave, e a guarda do contrato de lista não tinha como enxergá-la. Foi por
// isso que a dívida sobreviveu às seis fatias anteriores da TUT-204.
//
// `inativos` é `true | undefined`, e não `boolean`: o desligado é a AUSÊNCIA da
// chave. Um `?inativos=0` na URL seria estado ligado gravando "desligado", e a
// primeira tela a esquecer de apagar a chave passaria a mostrar o filtro
// marcado sem estar.
// ---------------------------------------------------------------------------

/** A lista de processos: os filtros combináveis refletidos na URL (TUT-122). */
export type ProcessosSearch = {
  q?: string;
  status?: string;
  claimant?: string;
  /** Repete na chave (`?subjectId=a&subjectId=b`); semântica "contém todos". */
  subjectId?: string[];
  /** TUT-240 — o cliente, por id de organização (não texto). */
  cliente?: string;
  /** TUT-248 fase 2 — VARA e RECLAMADA, por texto (colunas livres do processo). */
  vara?: string;
  reclamada?: string;
  /**
   * TUT-248 fase 2 — a faixa do VALOR DA CAUSA, em CENTAVOS nas duas pontas.
   *
   * Centavos na URL, e não reais, porque é o mesmo número que segue para a
   * API e para o banco: pôr reais aqui criaria um ponto flutuante no meio do
   * caminho do único campo que a casa guarda em centavos justamente para
   * nunca tê-lo. Quem traduz para "1.234,56" é o campo, na tela.
   */
  valorMin?: number;
  valorMax?: number;
  from?: string;
  to?: string;
  /**
   * TUT-476 — só os processos ABERTOS (fora de concluído e arquivado): o
   * recorte com que "Assuntos periciais" conta. `true | undefined`, como todo
   * filtro ligado/desligado: o desligado é a ausência da chave.
   */
  abertos?: true;
  /**
   * TUT-476 — o período de ENTRADA do processo (dias `AAAA-MM-DD`, inclusivos,
   * no fuso do escritório): o ponto da Série temporal "Processos novos".
   */
  periodoDe?: string;
  periodoAte?: string;
  /**
   * TUT-204a — a ordenação viaja na URL porque quem ordena é o SERVIDOR:
   * o link compartilhado reproduz a mesma lista, e a página deixa de
   * reordenar os 20 itens que já tinha em mãos.
   */
  sort?: ProcessSortColumn;
  dir?: "asc" | "desc";
  /** Ausente quando 1 — a primeira página não polui a URL. */
  page?: number;
  /**
   * TUT-538 (D28) — a vista da tela: `kanban` é o Kanban de processos (o
   * antigo `/kanban`, que redireciona para cá); ausente, a Lista.
   */
  vista?: "kanban";
};

/**
 * A Elaboração: a atividade, o recorte e os dois filtros, na URL (TUT-249).
 *
 * A tela guardava os quatro em `useState`, então **o link não viajava** — é o
 * mesmo defeito que a TUT-245 consertou na Agenda, e aqui ele dói igual: quem
 * manda "olha a fila de Laudos atrasados" mandava a fila de Todos sem recorte
 * nenhum, e o F5 apagava a escolha em silêncio.
 *
 * `atividade` entra junto dos filtros mesmo NÃO SENDO um: ela troca o QUADRO
 * (as três têm colunas diferentes), e é justamente por isso que precisa viajar
 * — o link de "Laudos" tem de abrir Laudos.
 */
export type ElaboracaoSearch = {
  atividade?: SelecaoDaElaboracao;
  /** TUT-313 — o ESCOPO "Atribuídos a mim", fora do grupo exclusivo. */
  meus?: true;
  recorte?: RecorteDaElaboracao;
  /** `ATRIBUÍDO À…` — id do usuário; quem casa é o servidor. */
  atribuido?: string;
  /** `CLIENTE` — id da organização, a mesma FK da TUT-240. */
  cliente?: string;
  /** TUT-291a — `RECLAMADA`, id da organização (`processes.defendantOrganizationId`). */
  reclamada?: string;
  /**
   * TUT-480 — o recorte da aba LAUDOS (que dá destino à Fila de laudos e à
   * Medição de laudos da Visão geral): as etapas (a chave repete na URL), o
   * REVISOR designado (id do usuário) e o período da DATA DE PROTOCOLO, em dias
   * `AAAA-MM-DD` do escritório. Só a aba Laudos os aplica; nas outras são
   * descartados no ponto de leitura.
   */
  etapa?: EtapaDeLaudo[];
  revisor?: string;
  periodoDe?: string;
  periodoAte?: string;
  /**
   * TUT-578 — o recorte da ENTREGA que os painéis de prazo abrem (a faixa, ou
   * `todas`, e o período em dias do escritório): o quadro mostra só o que está
   * em elaboração ou revisão com o prazo de entrega nele (`itensNaEntrega`).
   */
  entrega?: FiltroDeEntrega;
  entregaDe?: string;
  entregaAte?: string;
};

/** A fila de peças: os filtros combináveis refletidos na URL (TUT-179). */
export type PecasSearch = {
  q?: string;
  type?: LegalBriefTypeKey[];
  status?: LegalBriefStatusName[];
  mine?: boolean;
  dueFrom?: string;
  dueTo?: string;
  page?: number;
  sort?: string;
  dir?: "asc" | "desc";
};

/**
 * O detalhe do processo: `?aba=` resolvido para uma área existente. `peca` e
 * `laudo` (TUT-201 fase 2) apontam o item que a Linha quer em foco — a aba
 * rola até o card e o realça; id desconhecido simplesmente não realça nada.
 */
export type ProcessoDetalheSearch = {
  aba?: ProcessTab;
  peca?: string;
  laudo?: string;
};

/** As listas paginadas cuja tela já pede fatia (TUT-181 fatia 2). */
export type ListaComPaginaSearch = { q?: string; page?: number };

/**
 * TUT-204c — as listas que, além da fatia, ordenam no SERVIDOR.
 *
 * `sort` é `string` e não a união fechada da lista porque este tipo é comum às
 * cinco rotas da família A, e cada uma tem colunas próprias. Quem fecha o
 * conjunto é o `validateSearch` de cada rota, que sabe QUAL lista está
 * validando e chama `isColunaDaLista` com o nome dela — uma união comum
 * aceitaria `cidade` em `/peritos`, que o servidor recusaria com 400.
 */
export type ListaOrdenavelSearch = ListaComPaginaSearch & {
  sort?: string;
  dir?: ListSortDirection;
};

export type PropostaAssistenciaSearch = { id?: string };

/**
 * Configurações (TUT-283): `secao` é STRING crua de propósito — o catálogo
 * fechado das seções mora em `ConfiguracoesPage.tsx`, junto de `alcanca`, que
 * depende da sessão. Fazer a rota importar o catálogo puxaria a tela inteira
 * para o bundle principal (ela é `lazy`); manter o tipo aqui como texto e
 * revalidar no ponto de leitura é a mesma forma da `ProcessoDetalheSearch`.
 */
export type ConfiguracoesSearch = { secao?: string };

/**
 * TUT-485 — o Meu perfil. Além da seção, o link do e-mail de troca de e-mail
 * chega com o token em `confirmar-email`: texto cru, de uso único, que só o
 * servidor valida (e só para a sessão da dona do token).
 */
export type PerfilSearch = { secao?: string; "confirmar-email"?: string };

/**
 * TUT-491 — a caixa de entrada única: a conversa aberta (`c`, que diz a
 * fonte), os canais (a chave se repete), o recorte (Minhas / Não lidas; Todas
 * é a ausência) e a busca. `aba` e `conversa` são os nomes ANTIGOS (TUT-338):
 * a rota os lê só para redirecionar, sem quebrar link salvo.
 */
export type ConversasSearch = {
  c?: string;
  canal?: CanalDaConversa[];
  recorte?: "minhas" | "naoLidas";
  q?: string;
  aba?: string;
  conversa?: string;
};

/**
 * TUT-336 — as Configurações passaram a hospedar duas listas paginadas e
 * ordenáveis (Usuários e Registro de atividade). A busca, a página e a ordem
 * são da seção aberta, e a ordem só vale se a coluna existir na lista DELA.
 */
export type ConfiguracoesComListaSearch = ListaOrdenavelSearch & {
  secao?: string;
  /** TUT-337 — a aba da Central de Notificações e o filtro do histórico. */
  aba?: string;
  status?: string[];
};

export const LISTA_DA_SECAO: Readonly<Record<string, ListaOrdenavel>> = {
  usuarios: "users",
  atividade: "auditEvents",
  // TUT-337 — o histórico de envios da Central de Notificações.
  notificacoes: "notificationDeliveries",
};

/** TUT-336 — `/usuarios?secao=equipe` era a antiga "Equipe", hoje "Usuários". */
export const SECAO_DE_USUARIOS_ANTIGA: Readonly<Record<string, string>> = {
  equipe: "usuarios",
  convites: "convites",
  grupos: "grupos",
  armazenamento: "armazenamento",
};

/** Peritos: por escritório de origem, e o de mostrar quem foi desativado. */
export type PeritosSearch = ListaOrdenavelSearch & {
  empresa?: string;
  inativos?: true;
  /** TUT-580 — Tipo de pessoa (Física/Jurídica), Vínculo (Interno/Externo) e Portal. */
  pessoa?: TipoDePessoaFiltro;
  vinculo?: VinculoFiltro;
  portal?: PortalFiltro;
  // TUT-289: assistente (padrão do escritório) ou perito do juízo — ausente
  // mostra os dois.
  tipo?: "assistente" | "perito-do-juizo";
};

/**
 * TUT-580 — os filtros das colunas novas dos cadastros, na URL. A grafia da URL
 * é a da tela (`fisica`, `sem-convite`…); quem traduz para a API é
 * `lib/api/cadastros.ts`. Valor desconhecido some, como em todo filtro de URL.
 */
export type TipoDePessoaFiltro = "fisica" | "juridica";
export type VinculoFiltro = "interno" | "externo";
export type PortalFiltro = "sem-convite" | "convidado" | "ativo" | "revogado";
export type RegistroFiltro = "com" | "sem";

function umDe<T extends string>(valores: readonly T[]) {
  return (value: unknown): T | undefined =>
    typeof value === "string" && (valores as readonly string[]).includes(value) ? (value as T) : undefined;
}
export const tipoDePessoaFiltro = umDe<TipoDePessoaFiltro>(["fisica", "juridica"]);
export const vinculoFiltro = umDe<VinculoFiltro>(["interno", "externo"]);
export const portalFiltro = umDe<PortalFiltro>(["sem-convite", "convidado", "ativo", "revogado"]);
export const registroFiltro = umDe<RegistroFiltro>(["com", "sem"]);

/**
 * TUT-536 (D10) — Peritos do juízo: a busca, a ordem e a página de toda lista
 * ordenável, mais o de mostrar quem foi desativado (o mesmo parâmetro de
 * Assistentes). Não tem filtro de empresa nem de tipo: a tela é de um tipo só.
 */
export type PeritosDoJuizoSearch = ListaOrdenavelSearch & {
  inativos?: true;
  /** TUT-580 — com ou sem o registro profissional. */
  registro?: RegistroFiltro;
};

/** Advogados: o mesmo par, com o nome que a tela dele já usava na URL. */
export type AdvogadosSearch = ListaOrdenavelSearch & {
  escritorio?: string;
  inativos?: true;
  /** TUT-580 — Tipo de pessoa (Física/Jurídica) e Portal (Sem convite…Revogado). */
  pessoa?: TipoDePessoaFiltro;
  portal?: PortalFiltro;
};

/** Assuntos e Locadoras: só o "mostrar desativados". */
export type ListaComInativosSearch = ListaOrdenavelSearch & {
  inativos?: true;
};

/**
 * Agendamento (TUT-243): os dois filtros do quadro moram na URL, não em
 * `useState` — a guarda `list-filters-na-url.test.ts` cobra as duas metades
 * (a rota valida, a tela deriva). `atribuido` segue a grafia de `inativos`
 * (`?atribuido=1`, ausência = desligado); `cliente` é o `clientOrganizationId`
 * que a TUT-240 criou, texto porque a rota não conhece o catálogo do tenant.
 */
export type AgendamentoSearch = {
  atribuido?: true;
  cliente?: string;
  /**
   * TUT-313 — a pendência que FILTRA o quadro (o contador do topo clicado).
   * Lista fechada: as chaves das colunas do quadro que ainda se filtram
   * (`ehPendenciaQueFiltra`). TUT-530 (revisão): "pericia" e "protocolo" não
   * são mais coluna — a rota os traduz para `foco` (`focoDaPendenciaSemColuna`).
   */
  pendencia?: PendenciaQueFiltra;
  /**
   * TUT-244 — a PILHA de pop-ups, e ela mora aqui pelos mesmos três motivos
   * que os filtros: o "voltar" do navegador fecha o nível, o link fica
   * compartilhável (metade do valor do pop-up para a cliente) e a e2e abre o
   * segundo nível sem encenar dois cliques.
   *
   * `foco` é lista FECHADA (`CHAVES_DE_FOCO`); `processo` é identificador
   * opaco, texto — a rota não conhece o acervo do escritório, e quem descarta
   * o que não existe é a consulta da ficha.
   */
  foco?: ChaveDeFoco;
  processo?: string;
};

/**
 * TUT-568 — a LISTA do Agendamento (`/agendamento-lista`): os itens do Kanban
 * em tabela, mais as recusadas. Todos os filtros moram na URL (decisão do
 * dono): a busca, o status (a ausência é "Em andamento"), o cliente, o tipo, a
 * faixa da entrega (o recorte que os painéis da Visão geral abrem).
 */
export type AgendamentoListaSearch = {
  q?: string;
  /** TUT-578 — "entregues": as peças entregues e os laudos protocolados do período. */
  status?: "recusada" | "todos" | "entregues";
  cliente?: string;
  tipo?: TipoDeItem;
  entrega?: FiltroDeEntrega;
  /** TUT-578 — o período do prazo de entrega (a barra da Série "Entregas a vencer"). */
  entregaDe?: string;
  entregaAte?: string;
  /** TUT-578 — o período da ENTREGA, no status "entregues" (a Medição). */
  periodoDe?: string;
  periodoAte?: string;
  /** TUT-578 — só as entregues até o prazo (o `valor` da Medição). */
  noPrazo?: "sim";
};

/**
 * Propostas (TUT-276, na TUT-343): a lista já tinha `validateSearch`
 * ordenável (`buscaOrdenavel`); a PILHA de pop-ups dos quatro contadores de
 * propostas entra por cima, pelas mesmas três razões da TUT-244. Até a
 * TUT-343 ela morava em Clientes (`/organizacoes`), junto do quadro.
 */
export type PropostasSearch = ListaOrdenavelSearch & {
  foco?: ChaveDeFoco;
  processo?: string;
  /** TUT-488 — o período do quadro: de QUE data ele é, e de/até em `AAAA-MM-DD`. */
  periodo?: CampoDoPeriodoDePropostas;
  de?: string;
  ate?: string;
  /**
   * TUT-584 — os filtros avançados, no padrão das listas: a chave se repete
   * ("ou" dentro do campo, "e" entre campos). `status` e `modalidade` são
   * listas fechadas; `cliente` é o id da organização.
   */
  status?: string[];
  modalidade?: string[];
  cliente?: string[];
};

/** TUT-488 — a data a que o período do quadro de Propostas se refere. */
export const CAMPOS_DO_PERIODO_DE_PROPOSTAS = ["criada", "enviada"] as const;
export type CampoDoPeriodoDePropostas = (typeof CAMPOS_DO_PERIODO_DE_PROPOSTAS)[number];

/**
 * O período do quadro de Propostas, lido da busca crua como lista fechada:
 * campo desconhecido e dia que não existe no calendário (`2026-02-31`, que a
 * API recusaria com 400) são descartados, e não repassados ao servidor.
 */
export function periodoDePropostasDaBusca(busca: Record<string, unknown>): {
  periodo?: CampoDoPeriodoDePropostas;
  de?: string;
  ate?: string;
} {
  const periodo = CAMPOS_DO_PERIODO_DE_PROPOSTAS.find((campo) => campo === busca.periodo);
  const de = diaDeCalendario(busca.de);
  const ate = diaDeCalendario(busca.ate);
  return {
    ...(periodo ? { periodo } : {}),
    ...(de ? { de } : {}),
    ...(ate ? { ate } : {}),
  };
}

/**
 * Financeiro (TUT-276): a tela é só o resumo — sem lista própria, sem
 * ordenação —, então a busca dela carrega SÓ a pilha dos quatro contadores do
 * topo.
 */
export type FinanceiroSearch = {
  foco?: ChaveDeFoco;
  processo?: string;
};

/**
 * Agenda (TUT-245): a visão, os cinco filtros da cliente e o compromisso
 * aberto — tudo na URL.
 *
 * Antes desta fatia `/agenda` não tinha `validateSearch` nenhum: a visão e os
 * filtros viviam em `useState` + `localStorage`, então o link NÃO viajava —
 * quem mandasse a tela para um colega mandava outra agenda. É o mesmo defeito
 * que a TUT-204g pagou nas listas, com um agravante: a agenda é a tela que
 * mais se manda por mensagem ("olha o dia 22").
 *
 * `visao` e `tipo` são listas FECHADAS, conferidas aqui e DE NOVO no ponto de
 * leitura — o `useSearch()` da filha vem mesclado com a busca do pai, que não
 * valida nada (a lição da TUT-204g).
 *
 * `assunto`, `atribuido` e `cliente` são identificadores OPACOS: a rota não
 * conhece o catálogo de assuntos, a equipe nem os clientes do escritório, e
 * quem descarta o que não existe é a tela — o mesmo desenho do `veiculo` da
 * TUT-204h. `local` é texto livre porque não há catálogo de locais.
 *
 * `compromisso` é o pop-up (o quarto cartão em que ela escreveu "POP UP"), e
 * mora aqui pelos mesmos motivos da pilha da TUT-244: o "voltar" do navegador
 * fecha o pop-up, o link abre o compromisso, e a e2e não precisa encenar o
 * clique no cartão certo do calendário.
 */
export type AgendaSearch = {
  visao?: AgendaView;
  // TUT-342 — os quatro filtros de lista são de escolha múltipla.
  tipo?: AgendaKind[];
  assunto?: string[];
  atribuido?: string[];
  cliente?: string[];
  local?: string;
  de?: string;
  ate?: string;
  compromisso?: string;
  /**
   * TUT-297 — reunião de 21/09: o padrão passou a ser só perícia e reunião;
   * prazo é ruído ("laudos/prazos", 29:00-30:00) e vira filtro que a pessoa
   * LIGA. Grafia canônica `?prazos=1`, mesma família de `inativos`/`atribuido`
   * do Agendamento — o desligado é a AUSÊNCIA da chave.
   */
  prazos?: true;
};

/**
 * Visão Geral (TUT-246): qual das duas visões, e a pilha de pop-ups.
 *
 * `?modo=detalhada` abre o painel montável; `?modo=simples`, o desenho dela
 * (sem `CONFIGURAR`). A URL é a verdade, e o navegador só lembra a ESCOLHA
 * para quando a chave está ausente: assim o link que alguém manda abre a
 * visão que ele viu, e não a que o destinatário escolheu da última vez.
 *
 * TUT-287 — a TUT-246 não escrevia `?modo=simples` ("seria estado dizendo o
 * padrão"). Mas a ausência NÃO é o padrão: é a escolha lembrada. Com a
 * lembrança em "detalhada", clicar em "simples" navegava para a MESMA busca
 * (sem `modo`), o roteador não navegava e a tela não mudava.
 */
export type VisaoGeralSearch = {
  modo?: "simples" | "detalhada";
  foco?: ChaveDeFoco;
  processo?: string;
  /** TUT-486 — o processo cuja ficha um item de widget abriu (D9). */
  ficha?: string;
};

/** Pessoas: o papel profissional (perito, advogado, …). */
export type PessoasSearch = ListaOrdenavelSearch & { papel?: string };

/**
 * TUT-334 — Contas a Pagar: a FAIXA (vencidas, vencem em 7 dias…). A rota só
 * garante texto; a lista fechada é conferida na tela (`isFaixaDeContaAPagar`),
 * como o papel em Pessoas.
 */
export type ContasAPagarSearch = ListaOrdenavelSearch & {
  /**
   * Situação (TUT-483): uma ou mais faixas, repetidas na chave. O cartão do
   * topo escolhe UMA; o filtro avançado soma. Valor único antigo continua valendo.
   */
  faixa?: string[];
  /** TUT-483 — Representante, por id; "ou" entre eles. */
  representante?: string[];
  /** TUT-483 — Processo: o número contém o texto. */
  processo?: string;
  /** TUT-483 — Vencimento de/até, `AAAA-MM-DD`. */
  de?: string;
  ate?: string;
  /** TUT-483 — Valor mín/máx em CENTAVOS nas duas pontas (como em /processos). */
  valorMin?: number;
  valorMax?: number;
  /** TUT-585 — a conta recém-criada que o aviso "Ver" destaca; não é filtro nem entra na visão salva. */
  destaque?: string;
};

/**
 * TUT-483 — Contas a Receber: a lista ganhou a URL das outras. Situação,
 * Cliente, Marco e Rota repetem na chave ("ou" dentro do campo, "e" entre
 * campos); Processo é busca; Valor é em centavos. As listas fechadas (situação,
 * marco, rota) são conferidas aqui; Cliente é o id da organização (UUID), e um
 * id que já não existe devolve a lista vazia — o "Limpar filtros avançados" o tira.
 */
export type ContasAReceberSearch = ListaOrdenavelSearch & {
  status?: string[];
  cliente?: string[];
  marco?: string[];
  rota?: string[];
  processo?: string;
  valorMin?: number;
  valorMax?: number;
  /** TUT-506 — Vencimento de/até (`AAAA-MM-DD`) e a faixa do vencimento da faturada. */
  de?: string;
  ate?: string;
  vencimento?: string[];
  /** TUT-585 — ver `ContasAPagarSearch`. */
  destaque?: string;
};

/**
 * Documentos: a classe do documento e o prazo de guarda (TUT-204i).
 *
 * Em português, como `empresa` e `papel`: a URL é do produto, o parâmetro é do
 * serviço (`category`/`retentionClass`), e a tela traduz um no outro.
 */
export type DocumentosSearch = ListaOrdenavelSearch & {
  /**
   * TUT-489a — os filtros por coluna da central são LISTAS (P30): a chave se
   * repete na URL, e o valor único antigo (`?classe=PLEADING`) continua
   * valendo. As listas fechadas são conferidas na TELA, como antes.
   */
  classe?: string[];
  retencao?: string[];
  /**
   * No PLURAL de propósito: `processo` já é chave de outras rotas (a Visão
   * geral e o Financeiro), como texto, e a busca do roteador é a união de
   * todas — a lista aqui quebraria quem navega mesclando a busca anterior.
   */
  processos?: string[];
  status?: string[];
  sigilo?: string[];
  /** TUT-489b — as etiquetas marcadas (ids do catálogo do escritório). */
  etiqueta?: string[];
  /** "Última atualização": nos últimos N dias (7, 30 ou 90). */
  periodo?: string;
  /** O documento aberto na prévia lateral (link compartilhável). */
  documento?: string;
};

/**
 * Viagens: a modalidade e o veículo, os dois aplicados pelo SERVIDOR.
 *
 * TUT-204h — `veiculo` em português como `empresa` e `escritorio`, e não
 * `vehicleId` como a API: a URL é do produto, o parâmetro é do serviço, e a
 * tela traduz um no outro. O `mode` ficou em inglês por já existir antes.
 */
export type ViagensSearch = ListaOrdenavelSearch & {
  mode?: VehicleTripMode;
  veiculo?: string;
};

/** Modalidade de viagem válida, ou ausente — a lista fechada é o `TRIP_MODES`. */
export function modalidadeDeViagem(valor: unknown): VehicleTripMode | undefined {
  return typeof valor === "string" &&
    (TRIP_MODES as readonly string[]).includes(valor)
    ? (valor as VehicleTripMode)
    : undefined;
}

/**
 * O `validateSearch` das listas ordenáveis, escrito UMA vez.
 *
 * As cinco rotas da família A têm o mesmo corpo, mudando só o nome da lista.
 * Cinco cópias seriam cinco chances de uma esquecer o descarte da coluna
 * inválida — e o sintoma disso não é erro: é a tela pedindo ao servidor algo
 * que ele recusa, com a lista vazia e nenhuma explicação na página.
 *
 * O descarte é deliberado e não é redundante com o `@IsIn` do DTO: a URL é
 * entrada do usuário e pode ser digitada à mão. Quando a coluna não existe, a
 * tela **ignora** e mostra a ordem padrão, em vez de mandar ao servidor algo
 * que já se sabe inválido.
 */
export function buscaOrdenavel<
  L extends ListaOrdenavel,
  S extends ListaOrdenavelSearch = ListaOrdenavelSearch,
>(
  // `qual`, e não `lista`: `lista(value)` já é o coercitivo de array desta
  // mesma pasta, e sombreá-lo faria o próximo leitor procurar o helper errado.
  qual: L,
  busca: Record<string, unknown>,
  // TUT-204g — os filtros DA TELA, cada um com o coercitivo que o valida.
  //
  // Antes desta fatia eles não passavam por aqui: caíam no `...resto` e
  // seguiam para a URL exatamente como vieram. O `...resto` continua existindo
  // e continua necessário (é ele que preserva o harness `?__tutelae_portal=1`
  // e qualquer parâmetro que não seja desta tela — a lição do `setSearchParams`
  // registrada no AGENTS), mas agora ele carrega só o que NINGUÉM declarou.
  // Chave declarada sai do resto e entra validada, ou não entra.
  filtros: Readonly<Record<string, (valor: unknown) => unknown>> = {},
): S {
  const { q, sort, dir, page, ...resto } = busca;
  const declarados: Record<string, unknown> = {};
  for (const [chave, coercitivo] of Object.entries(filtros)) {
    delete resto[chave];
    const valor = coercitivo(busca[chave]);
    if (valor !== undefined) declarados[chave] = valor;
  }
  return {
    ...resto,
    ...declarados,
    q: texto(q),
    ...(isColunaDaLista(qual, sort) ? { sort } : {}),
    ...(isDirecaoDeOrdenacao(dir) ? { dir } : {}),
    page: pagina(page),
  } as S;
}

/** A fila pericial (TUT-181): busca, status repetido na chave e página. */
export type PericiasSearch = {
  q?: string;
  status?: string[];
  /** TUT-527 (D32) — "Atribuídas a mim": as perícias de que quem vê é o assistente. */
  minhas?: boolean;
  /**
   * TUT-476 — o motivo de encerramento: `com` / `sem` motivo estruturado, ou o
   * id de UM motivo. Junto do `status` do desfecho, é o recorte de cada barra
   * de "Frustrações e remarcações".
   */
  motivo?: string;
  /**
   * TUT-476 — o período de AGENDAMENTO (dias `AAAA-MM-DD`, inclusivos): o ponto
   * da Série temporal "Perícias agendadas" e a Medição "Perícias realizadas".
   */
  periodoDe?: string;
  periodoAte?: string;
  page?: number;
  /** TUT-204c — ordenação de servidor; ver `ListaOrdenavelSearch`. */
  sort?: string;
  dir?: ListSortDirection;
};

/** A fila de prazos: busca, status único do contrato HTTP e página. */
export type PrazosSearch = {
  q?: string;
  status?: string;
  page?: number;
  /** TUT-204c — ordenação de servidor; ver `ListaOrdenavelSearch`. */
  sort?: string;
  dir?: ListSortDirection;  /** TUT-204j — o tipo do prazo e o responsável, aplicados no SERVIDOR. */
  tipo?: string;
  responsavel?: string;
  /**
   * TUT-475 — a faixa de vencimento (`24h`, `72h`, `restante` ou `janela`),
   * aplicada no SERVIDOR com o corte do widget. Lista fechada: valor fora
   * dela some aqui e é conferido de novo na tela (`isVenceDePrazo`).
   */
  vence?: VenceDePrazo;
  /**
   * TUT-475 — a janela com que o widget contou a faixa (1..30), que vale no
   * lugar da do escritório. Vem do link do widget e some junto com `vence`.
   */
  dias?: number;
  /**
   * TUT-476 — o período de VENCIMENTO (a Série temporal "Prazos vencendo") e o
   * de CONCLUSÃO com `noPrazo` (a Medição "Prazos concluídos no prazo"): dias
   * `AAAA-MM-DD`, inclusivos. A conclusão só vale com `status=completed`.
   */
  periodoDe?: string;
  periodoAte?: string;
  concluidoDe?: string;
  concluidoAte?: string;
  noPrazo?: true;
};

/**
 * TUT-476 — as chaves de busca que cada LISTA aceita na URL, fechadas contra
 * o tipo da rota (`satisfies`: chave que não existe em `PrazosSearch` nem
 * compila). É o que `dashboardDestinos.test.ts` lê para reprovar um destino de
 * widget que declara um filtro que a lista não conhece.
 */
export const CHAVES_DA_BUSCA = {
  "/prazos": [
    "q", "status", "page", "sort", "dir", "tipo", "responsavel", "vence", "dias",
    "periodoDe", "periodoAte", "concluidoDe", "concluidoAte", "noPrazo",
  ],
  // TUT-568 — a lista do Agendamento, para onde os painéis de prazo levam.
  // TUT-578 — o recorte da entrega por período e as Entregues (a Medição).
  "/agendamento-lista": [
    "q", "status", "cliente", "tipo", "entrega", "entregaDe", "entregaAte",
    "periodoDe", "periodoAte", "noPrazo",
  ],
  "/pericias": [
    "q", "status", "minhas", "motivo", "periodoDe", "periodoAte", "page", "sort", "dir",
  ],
  "/processos": [
    "q", "status", "claimant", "subjectId", "cliente", "vara", "reclamada",
    "valorMin", "valorMax", "from", "to", "abertos", "periodoDe", "periodoAte",
    "sort", "dir", "page",
    // TUT-538 — a vista (Lista ou Kanban), que a tela também aceita na URL.
    "vista",
  ],
  // TUT-480: o recorte da aba Laudos (etapa, quem elabora, revisor e protocolo).
  "/elaboracao": [
    "atividade", "meus", "recorte", "atribuido", "cliente", "reclamada",
    "etapa", "revisor", "periodoDe", "periodoAte",
    // TUT-578 — o recorte da entrega dos painéis de prazo (Quesitos e Laudos).
    "entrega", "entregaDe", "entregaAte",
  ],
  // TUT-497: as três listas restantes das visões salvas (`lib/visoes-de-lista.ts`).
  "/elaboracao-lista": ["q", "type", "status", "mine", "dueFrom", "dueTo", "sort", "dir", "page"],
  // TUT-584 — Propostas no padrão: o recorte vale para a tela inteira. A pilha
  // de pop-ups (`foco`, `processo`) NÃO é recorte e fica de fora da visão.
  "/propostas": ["q", "status", "modalidade", "cliente", "periodo", "de", "ate", "sort", "dir", "page"],
  "/financeiro/contas-a-pagar": [
    "q", "faixa", "representante", "processo", "de", "ate", "valorMin",
    "valorMax", "sort", "dir", "page",
  ],
  "/financeiro/contas-a-receber": [
    "q", "status", "cliente", "marco", "rota", "processo", "valorMin",
    "valorMax", "de", "ate", "vencimento", "sort", "dir", "page",
  ],
} as const satisfies {
  "/prazos": readonly (keyof PrazosSearch)[];
  "/agendamento-lista": readonly (keyof AgendamentoListaSearch)[];
  "/pericias": readonly (keyof PericiasSearch)[];
  "/processos": readonly (keyof ProcessosSearch)[];
  "/elaboracao": readonly (keyof ElaboracaoSearch)[];
  "/elaboracao-lista": readonly (keyof PecasSearch)[];
  "/propostas": readonly (keyof PropostasSearch)[];
  "/financeiro/contas-a-pagar": readonly (keyof ContasAPagarSearch)[];
  "/financeiro/contas-a-receber": readonly (keyof ContasAReceberSearch)[];
};

export function texto(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** A lista, ou a ausência da chave quando ela está vazia (TUT-342). */
export function seHouver<T>(itens: T[]): T[] | undefined {
  return itens.length ? itens : undefined;
}

export function lista(value: unknown): string[] | undefined {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) {
    const strings = value.filter(
      (item): item is string => typeof item === "string",
    );
    return strings.length ? strings : undefined;
  }
  return undefined;
}

/**
 * Chave ligada, ou ausente — o desligado não polui a URL (TUT-204g).
 *
 * A escrita canônica é `?inativos=1`, que é o que as telas já gravavam quando
 * o filtro viajava cru — links antigos continuam abrindo com a caixa marcada.
 * `true` também é aceito porque é o que o roteador serializa quando a página
 * navega com o valor booleano; sem isso, marcar a caixa gravaria
 * `?inativos=true` e a leitura seguinte devolveria `undefined`, desmarcando-a
 * sozinha no primeiro recarregamento.
 */
export function ligado(value: unknown): true | undefined {
  return value === "1" || value === true || value === "true" ? true : undefined;
}

/** Página ≥ 2, ou ausente — a primeira página não polui a URL. */
/**
 * Centavos vindos da URL (TUT-248 fase 2).
 *
 * Diferente de `pagina`, que tem um padrão sensato para cair (1), aqui **não
 * existe padrão**: uma faixa de valor que não dá para ler tem de sumir, não
 * virar zero — `?valorMin=abc` escondendo todo processo abaixo de R$ 0,00 seria
 * um filtro que ninguém pediu. Negativo e fracionário caem pelo mesmo motivo:
 * o campo é inteiro e não-negativo do outro lado, no DTO.
 */
export function centavos(value: unknown): number | undefined {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  // `Number("")` e `Number(" ")` são 0, e 0 é um inteiro válido: sem este
  // corte, `?valorMin=` ligaria o filtro em zero sozinho.
  if (typeof value === "string" && value.trim() === "") return undefined;
  const numero = Number(value);
  if (!Number.isInteger(numero) || numero < 0) return undefined;
  return numero;
}

export function pagina(value: unknown): number | undefined {
  const numero = Math.max(
    1,
    Number(typeof value === "string" || typeof value === "number" ? value : 1) ||
      1,
  );
  return numero > 1 ? numero : undefined;
}

// TUT-243: ASSISTANT_NOMINATION entra na lista fechada para o link de
// "Pendente assistente" do Agendamento (`/elaboracao-lista?type=ASSISTANT_NOMINATION`)
// não ser descartado pela validação. `typeOptions` de `LegalBriefsPage.tsx`
// continua com só três chips de propósito — a Elaboração não lista essa
// peça —, mas a URL aceita o tipo e a lista filtra corretamente por ele.
export const legalBriefTypes = new Set<LegalBriefTypeKey>([
  "IMPUGNATION",
  "QUESTIONS",
  "SUBSIDIES",
  "ASSISTANT_NOMINATION",
]);

export const legalBriefStatuses = new Set<LegalBriefStatusName>([
  "DRAFTING",
  "IN_REVIEW",
  "AWAITING_DELIVERY",
  "DELIVERED",
  "NOT_DELIVERED",
  "CANCELLED",
  // TUT-472 (Q071) — a impugnação que espera o laudo do perito (TUT-291a):
  // sem ela aqui, o filtro de /elaboracao-lista oferecia o status e a rota o descartava.
  "AWAITING_EXPERT_REPORT",
]);

export const tiposDeAssistente = new Set(["assistente", "perito-do-juizo"]);

export function tipoDeAssistente(
  value: unknown,
): "assistente" | "perito-do-juizo" | undefined {
  return typeof value === "string" && tiposDeAssistente.has(value)
    ? (value as "assistente" | "perito-do-juizo")
    : undefined;
}

export function legalBriefTypeList(value: unknown): LegalBriefTypeKey[] | undefined {
  const valid = lista(value)?.filter((item): item is LegalBriefTypeKey =>
    legalBriefTypes.has(item as LegalBriefTypeKey),
  );
  return valid?.length ? valid : undefined;
}

export function legalBriefStatusList(
  value: unknown,
): LegalBriefStatusName[] | undefined {
  const valid = lista(value)?.filter((item): item is LegalBriefStatusName =>
    legalBriefStatuses.has(item as LegalBriefStatusName),
  );
  return valid?.length ? valid : undefined;
}

/**
 * As listas dos filtros de contas valem o que o DTO da API aceita (TUT-483,
 * revisão 1). A URL é entrada do usuário: valor que o servidor recusaria com 400
 * não pode chegar à consulta, porque o 400 troca a página inteira pelo aviso de
 * erro e leva junto a barra e o popover. Então a rota DESCARTA o que o DTO
 * recusa, e a consulta sai sem ele.
 */

/** O teto de itens por chave repetida, o mesmo `ArrayMaxSize` do DTO. */
const TETO_DE_ITENS_DO_FILTRO = 50;
/** O teto da busca livre, o `MaxLength` do `search` dos DTOs de contas. */
export const TAMANHO_MAXIMO_DA_BUSCA = 120;
/** O teto do campo Processo, o mesmo `MaxLength` do DTO (e o `maxLength` do campo). */
export const TAMANHO_MAXIMO_DO_PROCESSO = 60;

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

/** Uma lista que só deixa passar o que `conhece`, sem vazios, no teto do DTO. */
function listaFechada(conhece: (valor: string) => boolean) {
  return (valor: unknown): string[] | undefined => {
    const validos = lista(valor)?.filter((item) => item !== "" && conhece(item)).slice(0, TETO_DE_ITENS_DO_FILTRO);
    return validos?.length ? validos : undefined;
  };
}

const idsDeOrganizacao = listaFechada((item) => UUID_V4.test(item));

function processoDoFiltro(valor: unknown): string | undefined {
  const preenchido = texto(valor);
  return preenchido !== undefined && preenchido.length <= TAMANHO_MAXIMO_DO_PROCESSO ? preenchido : undefined;
}

function diaDoFiltro(valor: unknown): string | undefined {
  return diaDeCalendario(valor);
}

/**
 * TUT-585 — o id da conta recém-criada (o aviso "Conta criada · Ver"). Vale
 * qualquer id de linha: uuid na API e ids legíveis na demonstração.
 */
function idDoDestaque(valor: unknown): string | undefined {
  return typeof valor === "string" && /^[0-9A-Za-z-]{1,64}$/u.test(valor) ? valor : undefined;
}

/** Os filtros de Contas a Pagar além da busca: o `validateSearch` da rota os lê daqui. */
export const filtrosDeContasAPagar = {
  faixa: listaFechada(isFaixaDeContaAPagar),
  representante: idsDeOrganizacao,
  processo: processoDoFiltro,
  de: diaDoFiltro,
  ate: diaDoFiltro,
  valorMin: centavos,
  valorMax: centavos,
  destaque: idDoDestaque,
} as const;

/** TUT-584 — os de Propostas (o período mora em `periodoDePropostasDaBusca`). */
export const filtrosDePropostas = {
  status: listaFechada(isStatusDeProposta),
  modalidade: listaFechada(isModalidadeDeProposta),
  // O id é opaco: a demonstração usa "org-001", e a API confere o UUID.
  cliente: listaFechada((item) => ID_OPACO.test(item)),
} as const;

const ID_OPACO = /^[0-9A-Za-z_-]{1,64}$/u;

/** Os de Contas a Receber. */
export const filtrosDeContasAReceber = {
  status: listaFechada(isSituacaoDeContaAReceber),
  cliente: idsDeOrganizacao,
  marco: listaFechada(isMarcoDeContaAReceber),
  rota: listaFechada(isRotaDeContaAReceber),
  processo: processoDoFiltro,
  valorMin: centavos,
  valorMax: centavos,
  // TUT-506 — o vencimento: período e faixa (vencidas, a vencer, sem vencimento).
  de: diaDoFiltro,
  ate: diaDoFiltro,
  vencimento: listaFechada(isFaixaDoVencimento),
  destaque: idDoDestaque,
} as const;

// ---------------------------------------------------------------------------
// TUT-491 — `/conversas`, a caixa de entrada única.
// ---------------------------------------------------------------------------

const CANAIS_DE_CONVERSA = new Set<string>(["whatsapp", "email", "outros", "equipe", "portal"]);
// O servidor confere o uuid do chat; aqui basta a forma (o modo demonstração
// usa ids legíveis, e a e2e roda nele).
const ID_DE_CONVERSA = /^(?:chat:[0-9A-Za-z-]{1,64}|cw:[1-9][0-9]{0,15})$/u;

/** Lista fechada nas quatro chaves; o resto atravessa (a página não é dona da query). */
export function validarBuscaDeConversas(busca: Record<string, unknown>): ConversasSearch {
  const { c, canal, recorte, q, ...resto } = busca;
  const id = texto(c);
  const canais = (lista(canal) ?? []).filter((valor) => CANAIS_DE_CONVERSA.has(valor)) as CanalDaConversa[];
  const termo = texto(q)?.slice(0, TAMANHO_MAXIMO_DA_BUSCA);
  return {
    ...resto,
    ...(id && ID_DE_CONVERSA.test(id) ? { c: id } : {}),
    ...(canais.length ? { canal: canais } : {}),
    ...(recorte === "minhas" || recorte === "naoLidas" ? { recorte } : {}),
    ...(termo ? { q: termo } : {}),
  } as ConversasSearch;
}

/**
 * O link antigo das duas abas (TUT-338): `?aba=atendimento` vira o filtro dos
 * canais externos, `?aba=chat` o de Equipe e Portal, e o `?conversa=701` do
 * atendimento vira `?c=cw:701`. `null` quando não há nada a migrar.
 */
export function migrarBuscaAntigaDeConversas(busca: ConversasSearch): ConversasSearch | null {
  const { aba, conversa, ...resto } = busca;
  if (aba === undefined && conversa === undefined) return null;
  const canal: CanalDaConversa[] | undefined =
    aba === "atendimento" ? ["whatsapp", "email", "outros"] : aba === "chat" ? ["equipe", "portal"] : resto.canal;
  const numero = typeof conversa === "string" && /^[1-9][0-9]{0,15}$/u.test(conversa) ? conversa : null;
  return {
    ...resto,
    ...(canal ? { canal } : {}),
    ...(numero && !resto.c ? { c: `cw:${numero}` } : {}),
  };
}
